import { useMemo } from 'react';
import dayjs from 'dayjs';
import type { AlertCardType, Kind } from '@/domain/Notifications/Notifications';
import type { TabKey, TabState } from '../types';
import { TAB_KEYS } from '../constants';

const normalize = (s: unknown) => (s ?? '').toString().toLowerCase();

const EXACT_SEVERITY = ['high level', 'low level', 'high pressure', 'low pressure'];

function matches(notification: AlertCardType, term: string): boolean {
	const q = term.trim().toLowerCase();
	if (!q) return true;

	const isSystem = notification.isHistoricalAlert === true;
	const isForecast = !isSystem;
	const titleLower = normalize(notification.title);
	const severityPhraseFromTitle = (() => {
		const m = titleLower.match(/alert:\s*(?:possible\s+)?(high|low)\s+([a-z ]+)/i);
		return m ? `${m[1]} ${m[2]}`.trim().toLowerCase() : '';
	})();
	if (/\b(high|low)\b/.test(q)) {
		if (!EXACT_SEVERITY.includes(q)) return false;
		return [titleLower, severityPhraseFromTitle].some((b) => b.includes(q));
	}
	const buckets = [
		titleLower,
		notification.locationName,
		notification.infrastructureName,
		notification.infrastructureType,
		notification.issueType,
		notification.parameterName,
		(notification as any).sectorName,
		isForecast ? 'forecast alert' : 'system alert',
		notification.type,
	]
		.map(normalize)
		.filter(Boolean);

	return buckets.some((b) => b.includes(q));
}

const sortByDateDesc = (a: AlertCardType, b: AlertCardType) => {
	const ta = dayjs(a.datetime, 'YYYY/MM/DD hh:mm:ss A', true).valueOf();
	const tb = dayjs(b.datetime, 'YYYY/MM/DD hh:mm:ss A', true).valueOf();
	return tb - ta;
};

export function useNotificationSearch(byTab: Record<Kind, TabState>, search: string) {
	const mergedByTab = useMemo(() => {
		const all = [...byTab.ALL.items].sort(sortByDateDesc);
		const system = [...byTab.ISSUES.items].sort(sortByDateDesc);
		const strategies = [...byTab.RECOMMENDATIONS.items].sort(sortByDateDesc);
		return { all, system, strategies };
	}, [byTab]);

	const filtered = useMemo(
		() => ({
			[TAB_KEYS.ALL]: mergedByTab.all.filter((n) => matches(n, search)),
			[TAB_KEYS.SYSTEM]: mergedByTab.system.filter((n) => matches(n, search)),
			[TAB_KEYS.FORECAST]: mergedByTab.strategies.filter((n) => matches(n, search)),
		}),
		[mergedByTab, search],
	);

	return filtered;
}
